import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { RCP_OPTIONS, RCP_DEFAUT, trouverCultureParLibelle } from '../../shared/constants';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import { useHorizon } from '../../shared/hooks/useHorizon';
import { libelleHorizon } from '../../shared/utils/horizon';
import LimiteErreur from '../../shared/components/LimiteErreur';
import TensionChart from '../../shared/charts/TensionChart';
import CourbesStatChart from '../../shared/charts/CourbesStatChart';

// Éléments suivis par la page, dans l'ordre d'empilement du graphique
// (le premier de la liste forme la base de la pile, cochons en bas, maïs en
// haut). `defaut` reprend la maquette : tout sauf le tournesol est coché à
// l'ouverture.
const ELEMENTS = [
  { cle: 'cochons', label: 'Cochons', defaut: true },
  { cle: 'vaches_laitieres', label: 'Vaches laitières', defaut: true },
  { cle: 'tournesol', label: 'Tournesol', defaut: false },
  { cle: 'mais_grain', label: 'Maïs', defaut: true },
];

// Surfaces proposées pour le maïs et le tournesol : celle de l'exploitation
// ('ma') ou une surface fixe en ha. Les résultats de ces deux cultures sont
// multipliés par la surface choisie.
const CHOIX_SURFACE = [
  { value: 'ma', label: 'Ma surface' },
  { value: '1', label: '1 ha' },
  { value: '5', label: '5 ha' },
  { value: '10', label: '10 ha' },
];

// Éléments de la réponse API dont les résultats dépendent d'une surface.
const FAMILLE_PAR_ELEMENT = { mais_grain: 'mais', tournesol: 'tournesol' };

const formaterHa = (ha) =>
  `${ha.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ha`;

/**
 * Clé d'horizon d'un élément de la réponse, au format de YEAR_COLORS /
 * CourbesStatChart : l'API peut écrire "2" ou "+2" en TRACC, on ramène tout
 * à "+2".
 */
function cleHorizonApi(rcp, annee) {
  const texte = String(annee);
  if (rcp !== 'tracc' || texte === 'reference' || texte.startsWith('+')) return texte;
  return `+${texte}`;
}

/**
 * Eau et irrigation / Tension.
 *
 * Route réelle confirmée (page/tension, apiPlumberFetch) : { idExploitation,
 * annee, rcp } -> { elements: [{ cle, label, type, points: [{date, valeur}] }] }.
 * L'API identifie chaque élément par `cle` directement ("mais_grain",
 * "tournesol", "cochons", "vaches_laitieres") — PAS par un idProduction/code
 * à retraduire, contrairement à ce qui avait été codé au départ. Elle
 * renvoie toujours les 4 éléments d'un coup, sans qu'on les lui demande ;
 * le filtre "affiché sur le graphique" (cases à cocher) est purement un
 * filtre d'AFFICHAGE côté front, appliqué après réception.
 */
export default function Tension() {
  const { idExploit } = useOAD();

  const [coches, setCoches] = React.useState(
    () => ELEMENTS.filter((e) => e.defaut).map((e) => e.cle)
  );
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);
  // Liste déroulante : 2035 / 2050 (ou +2 °C / +2.7 °C en TRACC), sans la
  // référence 2020. 2035 (ou +2 °C) est coché à l'ouverture.
  const [annee, setAnnee, anneesPossibles] = useHorizon(rcp);

  function basculer(cle) {
    setCoches((actuels) =>
      actuels.includes(cle)
        ? actuels.filter((c) => c !== cle)
        : // On réordonne selon ELEMENTS pour que l'ordre des barres ne dépende
          // pas de l'ordre dans lequel les cases ont été cochées.
          ELEMENTS.filter((e) => actuels.includes(e.cle) || e.cle === cle).map((e) => e.cle)
    );
  }

  // Assolement réel (page/production-rapport, déjà utilisée ailleurs).
  const { data: assolement, loading: assolementEnChargement } = useApiData(
    api.getProductionRapport,
    { idExploitation: idExploit }
  );

  // Surface (ha) de chaque famille dans l'assolement réel : somme des surfaces
  // de toutes les cultures de la famille (plusieurs maïs possibles). Format de
  // page/production-rapport : { "<libellé>": [surface, rendement, prix, PB] }.
  const surfacesExploitation = React.useMemo(() => {
    const totaux = { mais: 0, tournesol: 0 };
    Object.entries(assolement ?? {}).forEach(([libelle, valeurs]) => {
      const famille = trouverCultureParLibelle(libelle)?.famille;
      if (famille in totaux) totaux[famille] += Number(valeurs?.[0]) || 0;
    });
    return totaux;
  }, [assolement]);

  // Présence du maïs / du tournesol dans l'assolement. Tant que l'assolement
  // n'est pas chargé, on ne désactive rien (évite un flash "grisé").
  const aMais = !assolement || surfacesExploitation.mais > 0;
  const aTournesol = !assolement || surfacesExploitation.tournesol > 0;
  const presence = { mais: aMais, tournesol: aTournesol };

  // Une fois l'assolement connu :
  //  - culture absente -> case décochée (et désactivée, cf. estDesactive) ;
  //  - pas de maïs mais du tournesol -> on coche le tournesol à la place ;
  //  - ni maïs ni tournesol -> les deux cases sont décochées et désactivées.
  React.useEffect(() => {
    if (!assolement) return;
    setCoches((actuels) => {
      const suivants = new Set(actuels);
      if (!aMais) suivants.delete('mais_grain');
      if (!aTournesol) suivants.delete('tournesol');
      if (!aMais && aTournesol) suivants.add('tournesol');
      // Ordre d'empilement du graphique = celui de ELEMENTS.
      return ELEMENTS.filter((e) => suivants.has(e.cle)).map((e) => e.cle);
    });
  }, [assolement, aMais, aTournesol]);

  function estDesactive(cle) {
    // `cle` ici est celle des cases à cocher (ELEMENTS / la réponse API,
    // "mais_grain"), pas la famille de culture ("mais").
    if (cle === 'mais_grain') return !aMais;
    if (cle === 'tournesol') return !aTournesol;
    return false;
  }

  // Surface choisie dans les listes "Surface maïs" / "Surface tournesol" (ces
  // listes ne sont affichées que si la culture est dans l'assolement).
  const [choixSurface, setChoixSurface] = React.useState({ mais: 'ma', tournesol: 'ma' });
  const surfaceChoisie = (famille) =>
    choixSurface[famille] === 'ma'
      ? (assolement ? surfacesExploitation[famille] : 1)
      : Number(choixSurface[famille]);

  // Pas d'`annee` dans la requête : l'API renvoie tous les horizons d'un
  // coup, on bascule de l'un à l'autre en filtrant (voir elementsAffiches).
  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit, rcp }),
    [idExploit, rcp]
  );

  const { data: donneesApi, loading: tensionEnChargement, error } = useApiData(
    api.getTension,
    parametres
  );
  // On attend aussi l'assolement : sans lui, les surfaces ne sont pas connues et
  // les courbes maïs / tournesol bougeraient au chargement.
  const loading = tensionEnChargement || assolementEnChargement;

  // Résultats multipliés par la surface choisie : maïs par la surface maïs,
  // tournesol par la surface tournesol. Cochons et vaches laitières restent
  // inchangés. Les quatre statistiques sont toutes multipliées.
  const surfaceMais = surfaceChoisie('mais');
  const surfaceTournesol = surfaceChoisie('tournesol');
  const data = React.useMemo(() => {
    if (!donneesApi?.elements) return donneesApi;
    const surfaces = { mais: surfaceMais, tournesol: surfaceTournesol };
    return {
      ...donneesApi,
      elements: donneesApi.elements.map((element) => {
        const famille = FAMILLE_PAR_ELEMENT[element.cle];
        if (!famille) return element;
        const k = surfaces[famille];
        return {
          ...element,
          points: (element.points ?? []).map((p) => ({
            ...p,
            eau_moy: p.eau_moy * k,
            eau_q75: p.eau_q75 * k,
            eau_q90: p.eau_q90 * k,
            eau_max: p.eau_max * k,
          })),
        };
      }),
    };
  }, [donneesApi, surfaceMais, surfaceTournesol]);

  // Ne garde que l'horizon choisi (champ `Annee`) puis les éléments cochés,
  // dans l'ordre d'empilement attendu par TensionChart (celui de ELEMENTS,
  // pas celui renvoyé par l'API). En TRACC l'horizon de la sidebar vaut "2" /
  // "2.7" alors que l'API peut écrire "+2" / "+2.7" : on accepte les deux.
  const elementsAffiches = React.useMemo(() => {
    const cibles = [String(annee), `+${annee}`];
    const parCle = Object.fromEntries(
      (data?.elements ?? [])
        .filter((e) => cibles.includes(String(e.Annee)))
        .map((e) => [e.cle, e])
    );
    return ELEMENTS.filter((e) => coches.includes(e.cle) && parCle[e.cle]).map(
      (e) => parCle[e.cle]
    );
  }, [data, coches, annee]);

  // Tension cumulée des éléments cochés, pour TOUS les horizons (le choix
  // d'année de la sidebar ne concerne que le graphique du haut) :
  //  1. pour chaque horizon et chaque jour, on somme chaque statistique
  //     (moyen, Q75, Q90, max) sur les éléments cochés ;
  //  2. CourbesStatChart (prop `cumul`) additionne ensuite les jours entre
  //     eux, dans l'ordre des dates : 5 le 1er, 5 le 2 -> 10 le 2, etc.
  // `Years` = clé de couleur de l'horizon (2020/2035/2050, ou
  // "reference"/"+2"/"+2.7" en TRACC), comme sur les autres pages.
  const donneesTotal = React.useMemo(() => {
    const parHorizonEtJour = new Map();
    (data?.elements ?? [])
      .filter((e) => coches.includes(e.cle))
      .forEach((element) => {
        const horizon = cleHorizonApi(rcp, element.Annee);
        (element.points ?? []).forEach((p) => {
          const cle = `${horizon}|${p.date}`;
          const somme =
            parHorizonEtJour.get(cle) ??
            { Years: horizon, date_affichage: p.date, eau_moy: 0, eau_q75: 0, eau_q90: 0, eau_max: 0 };
          somme.eau_moy += p.eau_moy ?? 0;
          somme.eau_q75 += p.eau_q75 ?? 0;
          somme.eau_q90 += p.eau_q90 ?? 0;
          somme.eau_max += p.eau_max ?? 0;
          parHorizonEtJour.set(cle, somme);
        });
      });
    return [...parHorizonEtJour.values()];
  }, [data, coches, rcp]);

  // Libellé de l'horizon pour les titres : "en 2035", "à +2 °C", "en Référence".
  const horizonTitre =
    rcp === 'tracc' && annee !== 'reference'
      ? `à ${libelleHorizon(rcp, annee)}`
      : `en ${libelleHorizon(rcp, annee)}`;

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="eau-irrigation"
        niveau3="tension"
        idExploit={idExploit}
      >
        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Affiché sur le graphique :</legend>
          {ELEMENTS.map((element) => (
            <label key={element.cle} className="sidebar-checkbox">
              <input
                type="checkbox"
                checked={coches.includes(element.cle)}
                disabled={estDesactive(element.cle)}
                onChange={() => basculer(element.cle)}
              />
              <span>{element.label}</span>
            </label>
          ))}
        </fieldset>

        {[
          { famille: 'mais', libelle: 'Surface maïs' },
          { famille: 'tournesol', libelle: 'Surface tournesol' },
        ]
          // Pas de liste si la culture n'est pas dans l'assolement.
          .filter(({ famille }) => assolement && presence[famille])
          .map(({ famille, libelle }) => (
          <label key={famille} className="sidebar-champ">
            <span className="sidebar-label">{libelle}</span>
            <select
              value={choixSurface[famille]}
              onChange={(e) =>
                setChoixSurface((actuel) => ({ ...actuel, [famille]: e.target.value }))
              }
            >
              {CHOIX_SURFACE.map((choix) => (
                <option key={choix.value} value={choix.value}>
                  {choix.value === 'ma'
                    ? `${choix.label} (${formaterHa(surfacesExploitation[famille])})`
                    : choix.label}
                </option>
              ))}
            </select>
          </label>
        ))}

        <label className="sidebar-champ">
          <span className="sidebar-label">{rcp === 'tracc' ? 'Réchauffement' : 'Année'}</span>
          <select value={annee} onChange={(e) => setAnnee(e.target.value)}>
            {anneesPossibles.map((a) => (
              <option key={a} value={a}>{libelleHorizon(rcp, a)}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-tension"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Besoin en eau supplémentaire {horizonTitre}</h2>

          {error && <p className="tdb-erreur">{error}</p>}

          {loading ? (
            <p className="tdb-chargement">Chargement…</p>
          ) : (
            !error && <LimiteErreur cle={`${annee}-${rcp}`}>
                <div className="oad-largeur-eau">
                  <TensionChart data={elementsAffiches} />
                </div>
              </LimiteErreur>
          )}
        </section>

        {/* Total cumulé des éléments cochés, pour les 3 horizons, avec les 4
            statistiques. */}
        <section className="oad-card">
          <h2 className="tdb-section-titre">
            Besoin en eau supplémentaire cumulé sur les éléments cochés
          </h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading ? (
            <p className="tdb-chargement">Chargement…</p>
          ) : (
            !error &&
            (donneesTotal.length === 0 ? (
              <p className="tdb-chargement">Sélectionnez au moins un élément à afficher.</p>
            ) : (
              <LimiteErreur cle={`total-${rcp}`}>
                <CourbesStatChart
                  data={donneesTotal}
                  prefixe="eau"
                  cleDate="date_affichage"
                  // Cumul de m³/j jour après jour : on obtient des m³.
                  unite="m³"
                  nomAxe="m³"
                  statsParDefaut={['moy', 'q90']}
                  cumul
                  afficherTableau={false}
                />
              </LimiteErreur>
            ))
          )}
        </section>
      </main>
    </div>
  );
}
